import { useSelector } from "react-redux";
import debounce from "lodash.debounce";

// Helpers
import { handleError } from "@common/components/helpers/error";

// APIs
import CustomerInteractionClient from "@common/api/CustomerInteractionClient";

// Actions
import CustomerAction from "@common/redux/actions/CustomerAction";
import LoadingAction from "@common/redux/actions/LoadingAction";

// Components
import { mapStatusCounts } from "../helpers/forms";
import useLogin from "./useLogin";

const useCustomers = () => {
  const data = useSelector((state) => CustomerAction.getStatePath(state));
  const [{ site }] = useLogin();

  const {
    showFilters,
    searchText,
    selectedSites,
    reviewStatusIds,
    pageNumber,
    pageSize,
    selectedCustomer,
    onlyBannedCustomers,
    interactionDateFrom,
    interactionDateTo,
    updatedSortColumn,
  } = data;

  const setState = (data) => CustomerAction.dispatch(data);

  const recordsCallback = (records, totalRecords, reviewStatusCounts) => {
    setState({
      records,
      totalRecords,
      reviewStatusCounts,
    });
  };

  const galleryRecordsCallback = (filters, response) => {
    // Determine which record to update based on filters
    if (filters.onlyVulnerableCustomers) {
      setState({
        recordsOnlyVulnerableCustomer: { ...response },
      });
    } else if (filters.onlyTemporaryBreakCustomers) {
      setState({
        recordsOnlyTemporaryBreakCustomer: {
          ...response,
        },
      });
    } else if (filters.onlyBannedCustomers) {
      setState({
        recordsOnlyBannedCustomer: { ...response },
      });
    }
  };

  const params = (filters) => {
    return {
      reviewStatusIds,
      pageSize,
      customerId: selectedCustomer,
      pageNumber,
      onlyBannedCustomers,
      customerName: searchText,
      byCustomer: true,
      // Fall back to the site the user is logged in by default,
      // if no specific sites are selected
      siteIds:
        selectedSites?.length > 0 ? selectedSites : site ? [site?.id] : null,
      selectedEventType: [2], // customer interactions
      includeImages: false,
      includeThumbnails: false,
      interactionDateFrom: `${interactionDateFrom}T00:00:00Z`,
      interactionDateTo: `${interactionDateTo}T23:59:59Z`,
      sortColumns: [updatedSortColumn],
      ...filters,
    };
  };

  const getRecords = (filters, callback = recordsCallback) => {
    LoadingAction.set(true).then(() => {
      CustomerInteractionClient.Search(params(filters)).then(
        ({ data, reviewStatusCounts, totalRecords }) => {
          LoadingAction.set(false);
          const statusCounts = mapStatusCounts(reviewStatusCounts);
          callback(data, totalRecords, statusCounts);
        },
        (error) => handleError(error),
      );
    });
  };

  const getGalleryRecords = (filters, callback = galleryRecordsCallback) => {
    LoadingAction.set(true).then(() => {
      const updatedFilters = { ...filters, pageSize: 100, byCustomer: true };
      CustomerInteractionClient.Search(params(updatedFilters))
        .then((response) => {
          LoadingAction.set(false);
          callback(filters, response);
        })
        .catch((error) => {
          LoadingAction.set(false);
          handleError(error);
        });
    });
  };

  const getDiaryRecords = (filters) => {
    LoadingAction.set(true).then(() => {
      CustomerInteractionClient.Search(params(filters)).then(
        ({ data, reviewStatusCounts }) => {
          LoadingAction.set(false);
          const statusCounts = mapStatusCounts(reviewStatusCounts);
          setState({
            recordsForDiaryTab: data,
            reviewStatusCounts: statusCounts,
          });
        },
        (error) => handleError(error),
      );
    });
  };

  const onTabChange = (activeTab) => setState({ activeTab });

  const onInputChange = debounce(
    (_, { name, value }) => setState({ [name]: value }),
    500,
  );

  const toggleFilters = () => setState({ showFilters: !showFilters });

  return {
    ...data,
    setState,
    getRecords,
    getGalleryRecords,
    getDiaryRecords,
    onTabChange,
    onInputChange,
    toggleFilters,
  };
};

export default useCustomers;
